import React, { useState, useEffect, useCallback, useRef } from 'react';
import useSocketReconnect from '../../utils/useSocketReconnect';
import './MerxandatgeForm.css';
import PackComponentsEditor from './PackComponentsEditor';

// Helper function to generate clean image IDs
const generateCleanImageId = () => {
    const timestamp = Date.now();
    const randomPart = Math.random().toString(36).substring(2, 10);
    return `img-${timestamp}-${randomPart}`;
};
// Typical size suggestions for quick add
const TYPICAL_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

// Helper function to sort sizes according to typical order
const sortSizes = (sizes) => {
  if (!sizes || sizes.length === 0) return [];
  
  return [...sizes].sort((a, b) => {
    const indexA = TYPICAL_SIZES.indexOf(a.toUpperCase());
    const indexB = TYPICAL_SIZES.indexOf(b.toUpperCase());
    
    // If both are in the typical list, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    // If only A is in the list, it comes first
    if (indexA !== -1) return -1;
    // If only B is in the list, it comes first
    if (indexB !== -1) return 1;
    // If neither is in the list, sort alphabetically
    return a.localeCompare(b);
  });
};

// Helper for image compression
const compressImage = (file) => {
    return new Promise((resolveCompression) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const MAX_SIZE = 1200;
                let width = img.width;
                let height = img.height;
                if (width > height && width > MAX_SIZE) {
                    height = Math.round(height * (MAX_SIZE / width));
                    width = MAX_SIZE;
                } else if (height > MAX_SIZE) {
                    width = Math.round(width * (MAX_SIZE / height));
                    height = MAX_SIZE;
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
                resolveCompression(compressedDataUrl);
            };
            img.onerror = () => resolveCompression(null); // Handle image load errors
            img.src = event.target.result;
        };
        reader.onerror = () => resolveCompression(null); // Handle file read errors
        reader.readAsDataURL(file);
    });
};


function MerxandatgeForm({ socket, item, onClose, isEdit, catalog = [], initialKind = 'product' }) {
    const imagesLoaded = useRef(false);
    useSocketReconnect(socket, () => {
        socket.emit('.get_merxandatge_categories');
        if (isEdit && item?.id && !imagesLoaded.current) socket.emit('.get_merxandatge_images', item.id);
    });
    const isPack = (isEdit ? item?.kind : initialKind) === 'pack';
    const [packComponents, setPackComponents] = useState(() => (item?.pack_components || []).map(component => ({ itemId: component.itemId, quantity: component.quantity })));
    const [productData, setProductData] = useState({
        name: '',
        description: '',
        price: '',
        categories: [],
        active: true,
    });

    // Simplified image state: id, url (for existing), preview (for new/display), isNew
    const [images, setImages] = useState([]);
    const [removedImageIds, setRemovedImageIds] = useState([]); // Track IDs of *existing* images removed

    const [sizes, setSizes] = useState([]);
    const [newSize, setNewSize] = useState('');
    const [colors, setColors] = useState([]);
    const [newColor, setNewColor] = useState('');
    const [variants, setVariants] = useState([]);
    const [totalStock, setTotalStock] = useState(0);
    const [categoryInput, setCategoryInput] = useState('');
    const [allCategories, setAllCategories] = useState([]);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('basics');
    const [formStatus, setFormStatus] = useState(''); // General status messages
    const isInitialMount = useRef(true); // Track initial mount to prevent auto-generation on load

    // New: product type and preorder settings
    const [productType, setProductType] = useState('stock'); // 'stock' | 'made_to_order'
    const [paymentTiming, setPaymentTiming] = useState('pay_upfront'); // 'reserve_only' | 'pay_upfront'
    const [allowMultipleRounds, setAllowMultipleRounds] = useState(false);
    
    // First ronda data for made_to_order items
    const [firstRonda, setFirstRonda] = useState({
        startDate: '',
        endDate: ''
    });

    // Handle tab change function
    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    // --- Initialization Effects ---

    // Load existing data when editing
    useEffect(() => {
        let handleImages;
        imagesLoaded.current = false;
        if (isEdit && item) {
            setProductData({
                name: item.name || '',
                description: item.description || '',
                price: item.price?.toString().replace('.', ',') || '', // Use comma for display
                categories: item.categories || [],
                active: item.active !== undefined ? item.active : true,
            });

            const productTypeValue = item.type || 'stock';
            setProductType(productTypeValue);
            setPaymentTiming(item.payment_timing || 'pay_upfront');
            setAllowMultipleRounds(productTypeValue === 'made_to_order' ? true : !!item.allow_multiple_rounds);

            // Initialize variants and stock FIRST
            let initialVariants = [];
            let initialTotalStock = 0;
            
            if (item.variants && item.variants.length > 0) {
                // Parse stock values to ensure they're numbers
                const parsedVariants = item.variants.map(v => {
                    const stockValue = parseInt(v.stock);
                    return {
                        id: v.id,
                        size: v.size || '',
                        color: v.color || '',
                        stock: isNaN(stockValue) ? 0 : stockValue,
                        price: v.price !== null && v.price !== undefined ? v.price.toString().replace('.', ',') : '',
                        isNew: false // Mark as existing from DB
                    };
                });
                
                initialTotalStock = parsedVariants.reduce((sum, v) => sum + v.stock, 0);
                initialVariants = parsedVariants;
                
                console.log("Inicializando variantes con stock:", parsedVariants);
                setVariants(parsedVariants);
                setTotalStock(initialTotalStock);
            } else {
                // Create a default variant placeholder if none exists from DB
                const defaultStock = parseInt(item.total_stock) || 0;
                setTotalStock(defaultStock);
                setVariants([{
                    id: `temp-${Date.now()}`, // Temp ID for new
                    size: '', color: '', stock: defaultStock, price: '', isNew: true
                }]);
            }

            // Set sizes/colors AFTER variants/stock
            setSizes(item.sizes || []);
            setColors(item.colors || []);

            // Request existing images for editing
            if (item.id) {
                setLoading(true);
                setFormStatus('Carregant imatges existents...');
                handleImages = (data) => {
                    if (data && data.itemId === item.id && data.images) {
                        imagesLoaded.current = true;
                        socket.off('.merxandatge_images', handleImages);
                        setLoading(false);
                        setFormStatus('');
                        // Map received images to the local state format
                        setImages(data.images.map(img => ({
                            id: img.id, // This will be 'main' or the DB ID
                            url: img.url, // Pre-signed URL from backend
                            preview: img.url, // Use URL for preview of existing
                            isNew: false // Mark as not new
                        })));
                    }
                };
                socket.on('.merxandatge_images', handleImages);
                socket.emit('.get_merxandatge_images', item.id);
            }
        } else {
            // Initialize with one empty variant for new products
            setVariants([{
                id: `temp-${Date.now()}`, size: '', color: '', stock: 0, price: '', isNew: true
            }]);
            setProductType('stock');
            setPaymentTiming('pay_upfront');
            setAllowMultipleRounds(false);
        }

        // Use setTimeout to ensure this runs after the current render cycle finishes
        const timer = setTimeout(() => {
            isInitialMount.current = false;
        }, 0);

        // Cleanup function for the timeout
        return () => {
            clearTimeout(timer);
            if (handleImages) socket.off('.merxandatge_images', handleImages);
        };
    }, [isEdit, item, socket]); // Removed 'fetchImages' from deps, handled inside effect

    // Fetch all existing categories
    useEffect(() => {
        const handleCategories = (data) => {
            if (data && data.categories) {
                setAllCategories(data.categories);
            }
        };
        socket.on('.merxandatge_categories', handleCategories);
        socket.emit('.get_merxandatge_categories');
        // Clean up listener if component unmounts before response
        return () => {
            socket.off('.merxandatge_categories', handleCategories);
        };
    }, [socket]);

    // --- Variant Generation Effect ---
    const generateVariantCombinations = useCallback(() => {
        console.log("Running generateVariantCombinations with current variants:", variants);
        const existingVariants = [...variants];
        const newGeneratedVariants = [];

        const createVariant = (size, color) => {
            const existing = existingVariants.find(v => v.size === size && v.color === color);
            if (existing) {
                console.log("Found existing variant:", existing);
                return existing; // Keep existing stock etc.
            } else {
                return {
                    id: `temp-${Date.now()}-${size}-${color}`,
                    size, color, stock: 0, price: '', isNew: true
                };
            }
        };

        if (sizes.length > 0 && colors.length > 0) {
            sizes.forEach(size => {
                colors.forEach(color => newGeneratedVariants.push(createVariant(size, color)));
            });
        } else if (sizes.length > 0) {
            sizes.forEach(size => newGeneratedVariants.push(createVariant(size, '')));
        } else if (colors.length > 0) {
            colors.forEach(color => newGeneratedVariants.push(createVariant('', color)));
        } else {
            // Ensure at least one default variant exists if no sizes/colors
             const defaultVariant = existingVariants.find(v => !v.size && !v.color);
             if (defaultVariant) {
                 newGeneratedVariants.push(defaultVariant);
             } else {
                 // Add a new default if none found (and no sizes/colors)
                newGeneratedVariants.push({
                     id: `temp-${Date.now()}-default`, size: '', color: '', stock: 0, price: '', isNew: true
                 });
            }
        }

        // Calculate total stock from new variants
        const newTotalStock = newGeneratedVariants.reduce((sum, v) => sum + (parseInt(v.stock) || 0), 0);
        
        console.log("Setting variants with stock:", newGeneratedVariants);
        setVariants(newGeneratedVariants);
        setTotalStock(newTotalStock); // Update total stock to match

    }, [sizes, colors, variants]); // Include variants in dependency array


    // Update variants when sizes or colors change
    useEffect(() => {
        // Skip variant generation on initial mount to prevent overwriting loaded data
        if (isInitialMount.current) {
            console.log("Skipping variant generation on initial mount");
            return;
        }

        console.log("Size/color change detected, regenerating variants");
        
        // Only auto-generate if sizes or colors are defined.
        // If both are empty, we rely on the manual/default variant logic within generateVariantCombinations.
        if (sizes.length > 0 || colors.length > 0) {
            generateVariantCombinations();
        } else {
            // If sizes and colors become empty, ensure we have just one default variant.
            // We might need to explicitly reset to a single default variant here.
            const defaultVariant = variants.find(v => !v.size && !v.color);
             if (defaultVariant) {
                 setVariants([defaultVariant]); // Keep existing default
             } else {
                 // Create a new default variant if clearing sizes/colors removed all variants
                 const defaultStock = totalStock;
                 setVariants([{
                     id: `temp-${Date.now()}-reset`, size: '', color: '', stock: defaultStock, price: '', isNew: true
                 }]);
             }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizes, colors]); // generateVariantCombinations is memoized, safe to exclude


    // --- Input Handlers ---

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'price') {
            const priceRegex = /^[0-9]*,?[0-9]*$/;
            if (value === '' || priceRegex.test(value)) {
                setProductData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setProductData(prev => ({ ...prev, [name]: value }));
        }
    };

    // --- Image Handlers (Simplified) ---

    const handleImageChange = async (e) => {
        setLoading(true);
        setFormStatus('Processant imatges...');
        const files = Array.from(e.target.files);
        const newImageObjects = [];

        for (const file of files) {
            const compressedPreview = await compressImage(file);
            if (compressedPreview) {
                newImageObjects.push({
                    id: generateCleanImageId(), // Unique ID for this new image
                    preview: compressedPreview, // Base64 data URL
                    url: null, // No existing URL
                    isNew: true
                });
            } else {
                 setErrors(prev => ({ ...prev, images: `No s'ha pogut processar l'arxiu: ${file.name}` }));
            }
        }

        setImages(prevImages => [...prevImages, ...newImageObjects]);
        setLoading(false);
        setFormStatus('');
         if (errors.images) { // Clear image error if new ones are added successfully
             setErrors(prev => ({ ...prev, images: undefined }));
         }
    };

    const handleRemoveImage = (imageIdToRemove) => {
        if (!window.confirm('Esteu segur que voleu eliminar aquesta imatge?')) {
            return;
        }

        const imageToRemove = images.find(img => img.id === imageIdToRemove);
        if (imageToRemove && !imageToRemove.isNew) {
            // If it's an existing image (not new), track its ID for deletion on the backend
            setRemovedImageIds(prev => [...prev, imageIdToRemove]);
        }

        // Remove from the local display state regardless
        setImages(prevImages => prevImages.filter(img => img.id !== imageIdToRemove));
    };

    const handleSetMainImage = (imageIdToSetMain) => {
        setImages(prevImages => {
            const index = prevImages.findIndex(img => img.id === imageIdToSetMain);
            if (index > 0) { // No need to move if already first
                const itemToMove = prevImages[index];
                const remainingItems = prevImages.filter((_, i) => i !== index);
                return [itemToMove, ...remainingItems];
            }
            return prevImages; // Return unchanged if not found or already main
        });
    };

    // --- Size/Color/Category Handlers (Mostly Unchanged) ---

    const handleAddSize = () => {
        if (newSize.trim() && !sizes.includes(newSize.trim())) {
            setSizes([...sizes, newSize.trim()]);
            setNewSize('');
        }
    };

    const handleQuickAddSize = (sizeToAdd) => {
        if (!sizes.includes(sizeToAdd)) {
            setSizes([...sizes, sizeToAdd]);
        }
    };

    const handleRemoveSize = (sizeToRemove) => {
        setSizes(sizes.filter(size => size !== sizeToRemove));
    };

    const handleAddColor = () => {
        if (newColor.trim() && !colors.includes(newColor.trim())) {
            setColors([...colors, newColor.trim()]);
            setNewColor('');
        }
    };

    const handleRemoveColor = (colorToRemove) => {
        setColors(colors.filter(color => color !== colorToRemove));
    };

        const handleAddCategory = () => {
        if (categoryInput.trim() && !productData.categories.includes(categoryInput.trim())) {
            const newCategory = categoryInput.trim();
            setProductData(prev => ({ ...prev, categories: [...prev.categories, newCategory] }));
            if (!allCategories.includes(newCategory)) {
                setAllCategories(prev => [...prev, newCategory].sort()); // Keep sorted
            }
            setCategoryInput('');
        }
    };

    const handleSelectCategory = (category) => {
        if (category && !productData.categories.includes(category)) {
            setProductData(prev => ({ ...prev, categories: [...prev.categories, category] }));
        }
    };

    const handleRemoveCategory = (categoryToRemove) => {
        setProductData(prev => ({ ...prev, categories: prev.categories.filter(cat => cat !== categoryToRemove) }));
    };

    // --- Variant/Stock Handlers ---

    const handleVariantStockChange = (variantId, newStock) => {
        const stockValue = parseInt(newStock) || 0;
        let newTotalStock = 0;
        
        const updatedVariants = variants.map(variant => {
            if (variant.id === variantId) {
                newTotalStock += stockValue;
                return { ...variant, stock: stockValue };
            } else {
                const currentStock = parseInt(variant.stock) || 0;
                newTotalStock += currentStock;
                return variant;
            }
        });
        
        setVariants(updatedVariants);
        setTotalStock(newTotalStock);
        
        if (errors.stock && newTotalStock > 0) {
            setErrors(prev => ({ ...prev, stock: undefined }));
        }
    };

    const handleVariantPriceChange = (variantId, newPrice) => {
        const priceRegex = /^[0-9]*,?[0-9]*$/;
        if (newPrice !== '' && !priceRegex.test(newPrice)) return;

        const updatedVariants = variants.map(variant => {
            if (variant.id === variantId) {
                return { ...variant, price: newPrice };
            }
            return variant;
        });
        setVariants(updatedVariants);
    };

    const distributeStock = () => {
        if (variants.length === 0 || totalStock <= 0) return;

        const baseStock = Math.floor(totalStock / variants.length);
        const remainder = totalStock % variants.length;
        let currentTotal = 0;

        const updatedVariants = variants.map((variant, index) => {
            const stock = baseStock + (index < remainder ? 1 : 0);
            currentTotal += stock;
            return { ...variant, stock };
        });

        setVariants(updatedVariants);
        // Ensure total stock matches exactly after distribution
        setTotalStock(currentTotal);
    };

     const handleAddVariant = () => {
        setVariants(prev => [
            ...prev,
            {
                id: `temp-${Date.now()}-manual`,
                size: sizes.length > 0 ? sizes[0] : '', // Default to first option or empty
                color: colors.length > 0 ? colors[0] : '',
                stock: 0,
                price: '',
                isNew: true // Mark as new for backend logic if needed
            }
        ]);
    };

    const handleRemoveVariant = (variantId) => {
        let newTotalStock = 0;
        const updatedVariants = variants.filter(variant => {
            if (variant.id !== variantId) {
                newTotalStock += parseInt(variant.stock) || 0;
                return true;
            }
            return false;
        });
        setVariants(updatedVariants);
        setTotalStock(newTotalStock);
         // If removing the last variant, consider adding a default one back?
         // Or let the validation handle the zero stock case.
         if (updatedVariants.length === 0) {
             generateVariantCombinations(); // Regenerate based on sizes/colors, might add default
         }
    };


    // --- Validation ---

    const validateForm = () => {
        const newErrors = {};
        if (!productData.name.trim()) newErrors.name = "El nom és obligatori";

        const priceString = productData.price.toString().replace(',', '.');
        const priceValue = parseFloat(priceString);
        if (!productData.price || isNaN(priceValue) || priceValue < 0) {
            newErrors.price = "El preu és obligatori i ha de ser un número igual o superior a 0";
        }

        // Require at least one image for new products OR if all existing were removed
        if (images.length === 0) {
            newErrors.images = "Cal afegir almenys una imatge";
        }

        // Only enforce stock when product type is 'stock'
        if (!isPack && productType === 'stock') {
            if (totalStock <= 0) {
                newErrors.stock = "L'estoc total ha de ser superior a 0";
            }
        }

        // Validate variant prices (must be non-negative if set)
        for (const v of variants) {
            if (v.price && v.price !== '') {
                const parsed = parseFloat(v.price.toString().replace(',', '.'));
                if (isNaN(parsed) || parsed < 0) {
                    newErrors.variantPrice = "El preu de variant ha de ser un número igual o superior a 0 o buit (preu base).";
                    break;
                }
            }
        }

         // Check for duplicate variant combinations
         const variantCombinations = new Set();
         variants.forEach(v => {
             const key = `${v.size || 'null'}-${v.color || 'null'}`;
             if (variantCombinations.has(key)) {
                 newErrors.variants = "No es poden tenir variants duplicades (mateixa talla i color).";
             }
             variantCombinations.add(key);
         });

        if (isPack && (packComponents.length < 2 || packComponents.some(component => !Number.isInteger(Number(component.quantity)) || Number(component.quantity) < 1 || Number(component.quantity) > 999))) {
            newErrors.components = 'Selecciona almenys dos articles i una quantitat vàlida per a cadascun.';
            setActiveTab('components');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // --- Submission ---

    const handleSubmit = (e) => {
        if (e) e.preventDefault();

        if (!validateForm()) {
            // Auto-switch to the first tab with an error
            setTimeout(() => {
                if (errors.name || errors.price) setActiveTab('basics');
                else if (errors.images) setActiveTab('images');
                else if (errors.variants || errors.stock || errors.variantPrice) setActiveTab('stock');
                else if (errors.categories) setActiveTab('categories');
                // Add other tabs if needed
                setFormStatus("Si us plau, corregeix els errors marcats.");
            }, 0);
            return;
        }

        setLoading(true);
        setFormStatus(isEdit ? 'Guardant canvis...' : isPack ? 'Creant pack...' : 'Creant article...');
        setErrors({}); // Clear previous errors

        // Prepare payload for the backend
        const payload = {
            // Basic data
            id: isEdit ? item.id : null, // Send ID only if editing
            name: productData.name,
            description: productData.description,
            price: productData.price.replace(',', '.'), // Send with dot decimal separator
            categories: productData.categories,
            active: productData.active,
            kind: isPack ? 'pack' : 'product',
            pack_components: isPack ? packComponents.map(component => ({ itemId: component.itemId, quantity: Number(component.quantity) })) : [],
            total_stock: isPack ? 0 : totalStock, // Send calculated total stock
            type: isPack ? 'stock' : productType,
            payment_timing: paymentTiming,
            allow_multiple_rounds: allowMultipleRounds,
            
            // First ronda data for made_to_order items
            firstRonda: !isPack && productType === 'made_to_order' ? {
                startDate: firstRonda.startDate || null,
                endDate: firstRonda.endDate || null
            } : null,

            // Variants: Send the current state of variants
             // Ensure IDs are handled correctly (send DB id for existing, null/temp for new)
             variants: isPack ? [] : variants.map(v => ({
                id: v.isNew ? null : v.id, // Send null for new variants, backend will generate UUID
                tempId: v.isNew ? v.id : null, // Send temp ID for reference if needed? maybe not.
                size: v.size || null, // Use null for empty size/color
                color: v.color || null,
                stock: parseInt(v.stock) || 0,
                price: v.price ? v.price.replace(',', '.') : null
            })),


            // Images: Send necessary info for backend processing
             // Map images to include base64 data only for new ones
            images: images.map(img => ({
                id: img.id, // Frontend generated ID for new, DB ID for existing ('main' or UUID)
                previewData: img.isNew ? img.preview.split(';base64,').pop() : null, // Only send base64 for new images
                isNew: img.isNew,
                // Order is determined by array position (main is index 0)
            })),

            // Removed Images: Send IDs of existing images that were removed
            removedImageIds: removedImageIds,
        };

        console.log('Sending payload to .save_merxandatge_item:', {
            ...payload,
            images: payload.images.map(img => ({...img, previewData: img.previewData ? '...' : null })) // Avoid logging large base64
        });


        // Use a single event for save/update
        socket.emit('.save_merxandatge_item', payload, (response) => {
            setLoading(false);
            if (response && response.success) {
                const successMessage = isEdit ? 'Canvis guardats correctament!' : isPack ? 'Pack creat correctament!' : 'Article creat correctament!';
                setFormStatus(successMessage);
                // Remove the DOM manipulation since we're handling the class in JSX
                // Optionally refresh data from backend before closing
                socket.emit('.get_all_merxandatge', true); // Refresh list with inactive items too if needed
                setTimeout(() => {
                    onClose(); // Close form after a short delay
                }, 1500);
            } else {
                setFormStatus('Error al guardar.');
                setErrors({ submit: response?.message || 'Hi ha hagut un error inesperat al servidor.' });
            }
        });

        // Add a timeout for the server response
        const timeoutId = setTimeout(() => {
            if (loading) { // Check if still loading
                setLoading(false);
                setFormStatus('Error: Temps d\'espera esgotat.');
                setErrors({ submit: "El servidor no ha respost. Pot ser degut a imatges grans o problemes de connexió. Intenta-ho de nou." });
            }
        }, 60000); // 60 seconds timeout (adjust as needed)

        // Cleanup timeout on response
         // Use a temporary handler for the specific response
         const handleSaveResponse = (response) => {
             clearTimeout(timeoutId);
             // The main logic is already inside the emit callback
             socket.off('.save_merxandatge_response', handleSaveResponse); // Clean up this specific listener
         };
         // Assuming the backend sends back a specific event like '.save_merxandatge_response'
         // Or better, rely solely on the callback provided to emit.
         // The callback approach is generally cleaner for request/response patterns.

         // Let's stick to the callback within emit for handling the response.
         // The cleanup logic needs to be associated with the callback firing.
         // We can enhance the callback in emit:
         /*
         socket.emit('.save_merxandatge_item', payload, (response) => {
             clearTimeout(timeoutId); // Clear timeout when response received
             setLoading(false);
             // ... rest of handling ...
         });
         */
         // The above modification is done directly in the emit callback.

    };


    // --- Render Logic ---

    // Determine if variants should be editable (based on sizes/colors defined)
    const allowVariantEdits = sizes.length > 0 || colors.length > 0;

    return (
        <div className="merxandatge-form-overlay">
            <div className="merxandatge-form-container">
                <div className="form-header">
                    <h2>{isPack ? (isEdit ? "Editar pack" : "Crear pack") : (isEdit ? "Editar article" : "Afegir nou article")}</h2>
                    <button className="close-button" onClick={onClose} disabled={loading}>×</button>
                </div>

                {formStatus && <div className={`form-status ${errors.submit ? 'error' : formStatus.includes('correctament') ? 'success' : ''}`}>
                    {formStatus.includes('correctament') && <span className="success-icon">✓</span>}
                    {formStatus}
                </div>}
                {errors.submit && <div className="form-error">{errors.submit}</div>}

                {/* TABS */}
                <div className="form-tabs">
                     {/* Simplified Tabs */}
                    <button className={`form-tab ${activeTab === 'basics' ? 'active' : ''}`} onClick={() => handleTabChange('basics')}>Bàsic</button>
                    
                    <button className={`form-tab ${activeTab === 'images' ? 'active' : ''}`} onClick={() => handleTabChange('images')}>Imatges</button>
                    <button className={`form-tab ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => handleTabChange('categories')}>Categories</button>
                    {isPack ? <button className={`form-tab ${activeTab === 'components' ? 'active' : ''}`} onClick={() => handleTabChange('components')}>Articles inclosos</button> : <>
                    <button className={`form-tab ${activeTab === 'variants' ? 'active' : ''}`} onClick={() => handleTabChange('variants')}>Variants</button>
                    <button className={`form-tab ${activeTab === 'stock' ? 'active' : ''}`} onClick={() => handleTabChange('stock')}>Estoc</button>
                    </>}
                    <button className={`form-tab ${activeTab === 'visibility' ? 'active' : ''}`} onClick={() => handleTabChange('visibility')}>Visibilitat</button>
                </div>

                <div className="form-content">
                    {/* Prevent default form submission, handle via button click */}
                    <form onSubmit={(e) => e.preventDefault()}>

                        {isPack && <div className={`tab-panel ${activeTab === 'components' ? 'active' : ''}`}>
                            <PackComponentsEditor components={packComponents} onChange={setPackComponents} catalog={catalog} itemId={item?.id} error={errors.components} />
                        </div>}

                        {/* Basic Info Panel */}
                        <div className={`tab-panel ${activeTab === 'basics' ? 'active' : ''}`}>
                            {/* Name */}
                            <div className="form-group">
                                <label>Nom *</label>
                                <input type="text" name="name" value={productData.name} onChange={handleInputChange} className={errors.name ? "error" : ""} placeholder={isPack ? "Nom del pack" : "Nom del producte"} />
                                {errors.name && <div className="error-message">{errors.name}</div>}
                            </div>
                            {/* Description */}
                             <div className="form-group">
                                <label>Descripció</label>
                                <textarea name="description" value={productData.description} onChange={handleInputChange} rows="4" placeholder={isPack ? "Descripció detallada del pack" : "Descripció detallada del producte"} />
                            </div>
                            {/* Price */}
                             <div className="form-group">
                                <label>{isPack ? "Preu base del pack (€) *" : "Preu (€) *"}</label>
                                <input type="text" name="price" value={productData.price} onChange={handleInputChange} className={errors.price ? "error" : ""} placeholder="0,00" />
                                {errors.price && <div className="error-message">{errors.price}</div>}
                                <small className="input-help-text">Utilitzeu coma com a separador decimal. Ex: 15,99</small>
                            </div>
                        </div>

                        {/* Stock Panel */}
                        <div className={`tab-panel ${activeTab === 'stock' ? 'active' : ''}`}>
                            <div className="form-group">
                                <label>Tipus d'ítem</label>
                                <div className="radio-row">
                                    <label htmlFor="type-stock">
                                        <input 
                                            type="radio" 
                                            id="type-stock"
                                            name="productType" 
                                            value="stock" 
                                            checked={productType === 'stock'} 
                                            onChange={() => {
                                                setProductType('stock');
                                                setAllowMultipleRounds(false);
                                            }} 
                                        />
                                        <div>
                                            <div style={{ fontWeight: '500' }}>Stock</div>
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                                Control d'estoc tradicional
                                            </div>
                                        </div>
                                    </label>
                                    <label htmlFor="type-made-to-order">
                                        <input 
                                            type="radio" 
                                            id="type-made-to-order"
                                            name="productType" 
                                            value="made_to_order" 
                                            checked={productType === 'made_to_order'} 
                                            onChange={() => {
                                                setProductType('made_to_order');
                                                setAllowMultipleRounds(true);
                                            }} 
                                        />
                                        <div>
                                            <div style={{ fontWeight: '500' }}>Sota comanda</div>
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                                                Recollida de demandes amb dates
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            
                            <div style={{ marginTop: '20px' }}>
                                {productType === 'made_to_order' ? (
                                    <>
                                        <div className="form-group">
                                            <label>Moment de pagament</label>
                                            <select value={paymentTiming} onChange={(e) => setPaymentTiming(e.target.value)}>
                                                <option value="pay_upfront">Pagament immediat al fer la comanda</option>
                                                <option value="reserve_only">Pagament diferit (només reserva)</option>
                                            </select>
                                        </div>
                                        <div className="info-box">
                                            Les dates límit es gestionen des de la pantalla del producte.
                                        </div>
                                    </>
                                ) : (
                                    <div className="info-box">
                                        Els clients poden comprar mentre hi hagi estoc disponible.
                                    </div>
                                )}
                            </div>

                            {/* Stock Management (variants grid) */}
                            {productType === 'stock' && (
                                <>
                                    <div className="stock-header">
                                        <div className="stock-summary">
                                            <span className="stock-total">Estoc total calculat: <strong>{totalStock}</strong> unitats</span>
                                        </div>
                                        {errors.stock && <div className="error-message">{errors.stock}</div>}
                                        {errors.variants && <div className="error-message">{errors.variants}</div>}
                                        {errors.variantPrice && <div className="error-message">{errors.variantPrice}</div>}
                                    </div>

                                    <div className="variants-table-container">
                                        <table className="variants-table">
                                            <thead>
                                                <tr>
                                                    {sizes.length > 0 && <th>Talla</th>}
                                                    {colors.length > 0 && <th>Color</th>}
                                                    {(sizes.length === 0 && colors.length === 0) && <th>Variant</th>}
                                                    <th>Estoc *</th>
                                                    <th>Preu (€)</th>
                                                    <th>Accions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {variants.length === 0 ? (
                                                    <tr><td colSpan={(sizes.length > 0 ? 1 : 0) + (colors.length > 0 ? 1 : 0) + ((sizes.length === 0 && colors.length === 0) ? 1 : 0) + 3} className="no-variants">Definiu talles/colors o afegiu una variant manualment.</td></tr>
                                                ) : (
                                                    variants.map(variant => (
                                                        <tr key={variant.id}>
                                                            {sizes.length > 0 && (
                                                                <td>
                                                                    {allowVariantEdits ? (
                                                                        <select value={variant.size || ''} onChange={(e) => {
                                                                            const updatedVariants = variants.map(v => 
                                                                                v.id === variant.id ? { ...v, size: e.target.value } : v
                                                                            );
                                                                            setVariants(updatedVariants);
                                                                        }} disabled={loading}>
                                                                            <option value="">-- Cap --</option>
                                                                            {sortSizes(sizes).map(s => <option key={s} value={s}>{s}</option>)}
                                                                        </select>
                                                                    ) : ( variant.size || '--' )}
                                                                </td>
                                                            )}
                                                            {colors.length > 0 && (
                                                                <td>
                                                                    {allowVariantEdits ? (
                                                                        <select value={variant.color || ''} onChange={(e) => {
                                                                            const updatedVariants = variants.map(v => 
                                                                                v.id === variant.id ? { ...v, color: e.target.value } : v
                                                                            );
                                                                            setVariants(updatedVariants);
                                                                        }} disabled={loading}>
                                                                            <option value="">-- Cap --</option>
                                                                            {colors.map(c => <option key={c} value={c}>{c}</option>)}
                                                                        </select>
                                                                    ) : ( variant.color || '--' )}
                                                                </td>
                                                            )}
                                                            {(sizes.length === 0 && colors.length === 0) && (
                                                                <td> Article Base </td>
                                                            )}
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    value={variant.stock}
                                                                    onWheel={(event) => event.currentTarget.blur()}
                                                                    onChange={(e) => handleVariantStockChange(variant.id, parseInt(e.target.value) || 0)}
                                                                    className={errors.stock && (parseInt(variant.stock) || 0) <= 0 ? "error" : ""}
                                                                    disabled={loading}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    value={variant.price || ''}
                                                                    onChange={(e) => handleVariantPriceChange(variant.id, e.target.value)}
                                                                    placeholder={productData.price ? String(productData.price).replace('.', ',') : '—'}
                                                                    disabled={loading}
                                                                    style={{ width: '80px' }}
                                                                />
                                                            </td>
                                                            <td>
                                                                {variants.length > 1 && (
                                                                    <button type="button" className="remove-variant-button" onClick={() => handleRemoveVariant(variant.id)} disabled={loading}>Eliminar</button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}

                            {/* Deadline Management for made_to_order */}
                            {productType === 'made_to_order' && (
                                <div style={{ marginTop: '40px' }}>
                                    <div className="stock-header" style={{ marginBottom: '20px' }}>
                                        <div className="stock-summary">
                                            <span className="stock-total">Període de comandes</span>
                                        </div>
                                    </div>

                                    <div className="form-row" style={{ display: 'flex', gap: '50px' }}>
                                        <div className="form-group">
                                            <label>S'obre el</label>
                                            <input 
                                                type="datetime-local" 
                                                value={firstRonda.startDate}
                                                onChange={(e) => setFirstRonda(prev => ({...prev, startDate: e.target.value}))}
                                                disabled={loading}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Data límit</label>
                                            <input 
                                                type="datetime-local" 
                                                value={firstRonda.endDate}
                                                onChange={(e) => setFirstRonda(prev => ({...prev, endDate: e.target.value}))}
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    <div className="info-box" style={{ marginTop: '20px' }}>
                                        <p>Podeu afegir més períodes de comandes des de la pantalla del producte un cop creat.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Images Panel */}
                        <div className={`tab-panel ${activeTab === 'images' ? 'active' : ''}`}>
                             <div className="file-upload-container">
                                <label className="file-upload-button">
                                    {/* SVG Icon */}
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                    Afegir Imatges *
                                    <input type="file" accept="image/*" onChange={handleImageChange} className={errors.images ? "error" : ""} multiple disabled={loading}/>
                                </label>
                                {errors.images && <div className="error-message">{errors.images}</div>}
                            </div>
                            <div className="image-gallery">
                                {images.length > 0 ? images.map((image, index) => (
                                    <div key={image.id} className={`image-preview-container ${index === 0 ? 'main-image' : ''}`}>
                                        <img src={image.preview} alt="Preview" className="image-preview" />
                                        <div className="image-actions">
                                            {index !== 0 && (
                                                <button type="button" className="set-main-button" onClick={() => handleSetMainImage(image.id)} title="Establir com a imatge principal" disabled={loading}>★</button>
                                            )}
                                            <button type="button" className="remove-image-button" onClick={() => handleRemoveImage(image.id)} title="Eliminar imatge" disabled={loading}>×</button>
                                        </div>
                                        {index === 0 && <div className="main-image-badge">Principal</div>}
                                    </div>
                                )) : <div className="no-images">Cap imatge seleccionada</div>}
                            </div>
                             {images.length > 0 && <p className="image-help-text">La primera imatge és la principal. Feu clic a l'estrella (★) per canviar la imatge principal.</p>}
                        </div>

                        {/* Categories Panel */}
                        <div className={`tab-panel ${activeTab === 'categories' ? 'active' : ''}`}>
                            {/* Categories Content (reuse existing structure) */}
                            <div className="categories-container">
                                <div className="categories-section">
                                    <h3 className="section-title">Categories disponibles</h3>
                                    <div className="categories-list">
                                        {allCategories.map((category, index) => {
                                            const isSelected = productData.categories.includes(category);
                                            return (
                                                <button key={index} type="button" className={`category-tag ${isSelected ? 'selected' : ''}`} onClick={() => isSelected ? handleRemoveCategory(category) : handleSelectCategory(category)} disabled={loading}>
                                                    <span className="category-icon">{isSelected ? '✓' : '+'}</span>
                                                    <span className="category-name">{category}</span>
                                                </button>
                                            );
                                        })}
                                        {allCategories.length === 0 && <span className="no-categories">No hi ha categories disponibles</span>}
                                    </div>
                                </div>
                                <div className="categories-section">
                                    <h3 className="section-title">Afegir nova categoria</h3>
                                    <div className="tag-row">
                                        <input type="text" value={categoryInput} onChange={(e) => setCategoryInput(e.target.value)} placeholder="Nom de la nova categoria" className="tag-input" disabled={loading}/>
                                        <button type="button" className="add-tag-button" onClick={handleAddCategory} disabled={!categoryInput.trim() || loading}>Afegir</button>
                                    </div>
                                </div>
                             </div>
                             <div className="section-help-text"><p>Assigna categories per organitzar els {isPack ? 'packs' : 'productes'}.</p></div>
                        </div>

                        {/* Variants Panel */}
                        <div className={`tab-panel ${activeTab === 'variants' ? 'active' : ''}`}>
                            {/* Sizes */}
                            <div className="tag-input-container">
                                <label>Talles disponibles (opcional)</label>
                                <div className="tag-row">
                                    <input type="text" value={newSize} onChange={(e) => setNewSize(e.target.value)} placeholder="Afegir talla (S, M, etc.)" className="tag-input" disabled={loading}/>
                                    <button type="button" className="add-tag-button" onClick={handleAddSize} disabled={loading}>Afegir</button>
                                </div>
                                 {TYPICAL_SIZES.some(size => !sizes.includes(size)) && (
                                 <div className="suggestions-row" style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                     <span className="suggestions-label" style={{ color: '#6b7280', fontSize: '12px', marginRight: '4px' }}>Suggeriments:</span>
                                     {TYPICAL_SIZES.filter(size => !sizes.includes(size)).map((size) => (
                                         <button
                                             key={size}
                                             type="button"
                                             className="suggestion-chip"
                                             onClick={() => handleQuickAddSize(size)}
                                             disabled={loading}
                                             title="Afegir talla"
                                             style={{
                                                 padding: '4px 10px',
                                                 borderRadius: '6px',
                                                 border: '1px solid #d1d5db',
                                                 background: '#ffffff',
                                                 color: '#111827',
                                                 cursor: 'pointer'
                                             }}
                                         >
                                             {size}
                                         </button>
                                     ))}
                                 </div>
                                 )}
                                <div className="tags-container">
                                    {sortSizes(sizes).map((size, index) => (
                                        <div key={index} className="tag">{size}<button type="button" onClick={() => handleRemoveSize(size)} className="remove-tag-button" disabled={loading}>×</button></div>
                                    ))}
                                    {sizes.length === 0 && <div className="empty-message">No hi ha talles definides</div>}
                                </div>
                            </div>
                            {/* Colors */}
                            <div className="tag-input-container">
                                 <label>Colors disponibles (opcional)</label>
                                <div className="tag-row">
                                    <input type="text" value={newColor} onChange={(e) => setNewColor(e.target.value)} placeholder="Afegir color (Vermell, Blau, etc.)" className="tag-input" disabled={loading}/>
                                    <button type="button" className="add-tag-button" onClick={handleAddColor} disabled={loading}>Afegir</button>
                                </div>
                                <div className="tags-container">
                                    {colors.map((color, index) => (
                                        <div key={index} className="tag">{color}<button type="button" onClick={() => handleRemoveColor(color)} className="remove-tag-button" disabled={loading}>×</button></div>
                                    ))}
                                     {colors.length === 0 && <div className="empty-message">No hi ha colors definits</div>}
                                </div>
                            </div>

                             <div className="section-help-text"><p>Definiu talles i/o colors. Es generaran combinacions automàticament. Si no hi ha talles ni colors, hi haurà una sola variant.</p></div>
                        </div>


                        {/* Visibility Panel */}
                        <div className={`tab-panel ${activeTab === 'visibility' ? 'active' : ''}`}>
                             {/* Visibility Content (reuse existing structure) */}
                             <div className="visibility-control">
                                <label className="visibility-toggle-label">
                                    <input type="checkbox" checked={productData.active} onChange={(e) => setProductData(prev => ({ ...prev, active: e.target.checked }))} className="visibility-toggle-input" disabled={loading} />
                                    <span className="visibility-toggle-text">{isPack ? 'Pack' : 'Producte'} {productData.active ? 'visible a la botiga' : 'ocult'}</span>
                                </label>
                                <div className="visibility-description">
                                    <p>Un {isPack ? 'pack' : 'producte'} ocult no es mostrarà a la botiga però es conservarà al sistema.</p>
                                </div>
                             </div>
                        </div>

                        {/* Remove Upload Progress Section */}

                    </form>
                </div>

                <div className="form-actions">
                    <button type="button" className="cancel-button" onClick={onClose} disabled={loading}>Cancel·lar</button>
                    <button type="button" className="submit-button" onClick={handleSubmit} disabled={loading}>
                        {loading ? formStatus || 'Guardant...' : isEdit ? 'Guardar canvis' : isPack ? 'Crear pack' : 'Afegir article'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default MerxandatgeForm;
